export const RULE_ACTIONS = ["allow", "prevent", "warn", "not-performed"] as const;
export const RULE_OUTCOMES = ["blocked", "allowed", "warned", "check_failed"] as const;
export const RULE_FILTER_FIELDS = ["from", "to", "id", "client_id", "mid", "entity_type", "entity_value", "rule_name", "reference_id", "action", "outcome", "sandbox"] as const;
export type RuleFilters = Record<typeof RULE_FILTER_FIELDS[number], string>;
export const defaultRuleFilters = (): RuleFilters => ({ from: "", to: "", id: "", client_id: "", mid: "", entity_type: "", entity_value: "", rule_name: "", reference_id: "", action: "", outcome: "blocked", sandbox: "" });

export const getRuleBlocksRequest = (projectId: number, filters: RuleFilters, page: number, perPage: number) => {
	const data: Record<string, unknown> = {};
	for (const field of RULE_FILTER_FIELDS) {
		const value = filters[field].trim();
		if (!value) continue;
		if (["id", "client_id", "mid", "sandbox"].includes(field)) {
			if (!/^\d+$/.test(value) || !Number.isSafeInteger(Number(value))) throw new Error("invalidFilters");
			data[field] = Number(value);
		} else data[field] = value;
	}
	if (filters.from && filters.to && filters.from > filters.to) throw new Error("invalidDates");
	if (filters.action && !RULE_ACTIONS.includes(filters.action as typeof RULE_ACTIONS[number]) || filters.outcome && !RULE_OUTCOMES.includes(filters.outcome as typeof RULE_OUTCOMES[number]) || filters.sandbox && !["0", "1", "2", "3"].includes(filters.sandbox)) throw new Error("invalidFilters");
	if (!Number.isSafeInteger(page) || page < 0 || ![25, 50, 100].includes(perPage)) throw new Error("invalidFilters");
	return { method: "rules/list", expectedProjectId: projectId, headers: { ignoreBusy: "true" }, data: { ...data, pagination: { page, perPage } } };
};

export type RuleRecord = Record<string, unknown>;
export interface RuleList { rows: RuleRecord[]; total: number | null; totalPages: number | null; }
const isRecord = (value: unknown): value is RuleRecord => !!value && typeof value === "object" && !Array.isArray(value);
const count = (value: unknown): number | null => (typeof value === "number" || typeof value === "string" && value.trim() !== "") && Number.isSafeInteger(Number(value)) && Number(value) >= 0 ? Number(value) : null;

// The response schema is not documented yet. Keep adaptation here and retain every record field.
export const parseRuleBlocks = (payload: unknown): RuleList | null => {
	if (isRecord(payload) && "data" in payload && !Array.isArray(payload.data) && isRecord(payload.data)) payload = payload.data;
	const rows = Array.isArray(payload) ? payload : isRecord(payload) ? [payload.items, payload.rows, payload.list, payload.data].find(Array.isArray) : undefined;
	if (!Array.isArray(rows) || !rows.every(isRecord)) return null;
	const pagination = isRecord(payload) && isRecord(payload.pagination) ? payload.pagination : null;
	return { rows, total: count(pagination?.total), totalPages: count(pagination?.totalPages) };
};

export const RULE_TABLE_COLUMNS = ["created_at", "rule_name", "entity", "clientMerchant", "outcome"] as const;

export const ruleSummaryText = (value: unknown): string => typeof value === "object" ? "—" : ruleCellText(value);

export const ruleDateParts = (value: unknown, language: string) => {
	if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}(?:T| )/.test(value)) return null;
	const normalized = value.replace(" ", "T");
	const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized) ? normalized : `${normalized}Z`);
	if (!Number.isFinite(date.getTime())) return null;
	return {
		day: date.toISOString().slice(0, 10),
		date: date.toLocaleDateString(language, { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }),
		time: `${date.toLocaleTimeString(language, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: "UTC" })} UTC`,
	};
};

export const ruleAnalysisPeriod = (row: RuleRecord, filters: Pick<RuleFilters, "from" | "to">) => {
	const day = ruleDateParts(row.created_at, "en")?.day;
	const to = filters.to || day;
	if (!to || filters.from && filters.from > to) return null;
	const end = new Date(`${to}T00:00:00Z`);
	if (!Number.isFinite(end.getTime()) || end.toISOString().slice(0, 10) !== to) return null;
	// A narrow list filter should not hide the history needed for entity analysis.
	end.setUTCDate(end.getUTCDate() - 29);
	const monthFrom = end.toISOString().slice(0, 10);
	const from = filters.from && filters.from < monthFrom ? filters.from : monthFrom;
	return { from, to };
};

export const ruleCellText = (value: unknown): string => value === null || value === undefined || value === "" ? "—" : typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);
